// stores/strategiesStore.ts
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { StrategyWithVersions, StrategyVersion } from "@/lib/types";
import { api } from "@/lib/api";
import { useOrganizationsStore } from "./organizationsStore";

interface StrategiesState {
  strategiesByOrg: Record<string, StrategyWithVersions[]>;
  setStrategies: (
    organizationId: string,
    strategies: StrategyWithVersions[]
  ) => void;
  addStrategy: (organizationId: string, strategy: StrategyWithVersions) => void;
  updateStrategy: (
    organizationId: string,
    strategyId: string,
    updatedStrategy: Partial<StrategyWithVersions>
  ) => void;
  deleteStrategy: (organizationId: string, strategyId: string) => void;
  getStrategy: (strategyId: string) => StrategyWithVersions | undefined;
  getStrategyVersion: (
    strategyId: string,
    versionId: string
  ) => StrategyVersion | undefined;
  fetchStrategiesForCurrentOrg: () => Promise<void>;
}

export const useStrategiesStore = create<StrategiesState>()(
  immer((set, get) => ({
    strategiesByOrg: {},
    setStrategies: (organizationId, strategies) =>
      set((state) => {
        state.strategiesByOrg[organizationId] = strategies;
      }),
    addStrategy: (organizationId, strategy) =>
      set((state) => {
        if (!state.strategiesByOrg[organizationId]) {
          state.strategiesByOrg[organizationId] = [];
        }
        state.strategiesByOrg[organizationId].push(strategy);
      }),
    updateStrategy: (organizationId, strategyId, updatedStrategy) =>
      set((state) => {
        const strategies = state.strategiesByOrg[organizationId];
        if (strategies) {
          const index = strategies.findIndex((s) => s.id === strategyId);
          if (index !== -1) {
            Object.assign(strategies[index], updatedStrategy);
          }
        }
      }),
    deleteStrategy: (organizationId, strategyId) =>
      set((state) => {
        if (state.strategiesByOrg[organizationId]) {
          state.strategiesByOrg[organizationId] = state.strategiesByOrg[
            organizationId
          ].filter((s) => s.id !== strategyId);
        }
      }),
    getStrategy: (strategyId) => {
      const { strategiesByOrg } = get();
      const selectedOrgId =
        useOrganizationsStore.getState().selectedOrganizationId;
      if (!selectedOrgId) return undefined;
      return strategiesByOrg[selectedOrgId]?.find((s) => s.id === strategyId);
    },
    getStrategyVersion: (strategyId, versionId) => {
      const strategy = get().getStrategy(strategyId);
      return strategy?.versions.find((v) => v.id === versionId);
    },
    fetchStrategiesForCurrentOrg: async () => {
      const selectedOrgId =
        useOrganizationsStore.getState().selectedOrganizationId;
      if (!selectedOrgId) return;
      try {
        const strategies = await api.getOrganizationStrategies(selectedOrgId);
        set((state) => {
          state.strategiesByOrg[selectedOrgId] = strategies;
        });
      } catch (error) {
        console.error("Failed to fetch strategies:", error);
      }
    },
  }))
);
